/**
 * The shapes `/pl/fotograf-operator` renders, plus the closed icon set a specialist can
 * wear.
 *
 * The specialists and the cities themselves are NOT here any more. They live in
 * `specialist` / `specialist_deliverable` / `specialist_city` / `city` and are
 * loaded per request by `server/production-catalogue.ts`, so the panel's edits
 * reach the page — a hand-written array here meant an admin could change a rate,
 * watch it save, and see the site keep quoting the old one.
 *
 * City coordinates are WGS84 and feed the map projection in `poland-map.tsx`,
 * so pins land where the cities actually are rather than being eyeballed against
 * the outline. `@repo/core` bounds them to Poland's box on save.
 */
import {
  Camera,
  Clapperboard,
  type LucideIcon,
  Megaphone,
  Mic,
  MonitorPlay,
  PenTool,
  Scissors,
  Sparkles,
  Users,
  Video,
} from 'lucide-react'
import { YoutubeGlyph } from '@/components/atoms/youtube-glyph'
import { brandTone } from '@/lib/brand-tones'
import type { PlanView } from '@/lib/services'

/** One pin: a `city` row, reduced to what the UI draws. */
export type CityView = {
  id: string
  slug: string
  name: string
  /** Where the team is based vs. where it travels — drives the map legend. */
  note: string
  /** WGS84, used by the map projection. */
  lon: number
  lat: number
}

/**
 * Where a specialist can be booked.
 *
 * `"online"` is not "every city we listed" — it means the work never needs a
 * location at all, so the map highlights the whole country instead of pins.
 * Keeping it a separate variant stops it from decaying into a city list that
 * has to be extended every time a new city appears.
 */
export type Coverage = string[] | 'online'

/** One card: a `specialist` row with its deliverables and coverage resolved. */
export type SpecialistView = {
  id: string
  slug: string
  name: string
  blurb: string
  /** Ready to print: `od 250 zł`, or the quote-only sentence. */
  priceLabel: string
  /** Unit the price is quoted in, e.g. "godz.", "dzień zdjęciowy". */
  priceUnit: string
  /** Name from {@link SPECIALIST_ICONS}; unknown or `null` falls back. */
  icon: string | null
  /** What the client walks away with. Kept short — these render as a list. */
  deliverables: string[]
  coverage: Coverage
  /**
   * What the role is sold in, in catalogue order.
   *
   * For a shoot day that is the single `dzien-zdjeciowy` level; for the
   * consultation, the single hourly rate. Neither is a CHOICE any more — the
   * page used to make the visitor pick between „Cały plan (8 h)" and „Półplan
   * (3 h)", and the owner retired that split.
   *
   * Still a LIST rather than one level, because the catalogue may grow a second
   * one and the page reads whatever is there.
   */
  plans: PlanView[]
  /**
   * `pricing_unit = SHOOT_DAY` — somebody is booked onto a set for a day.
   *
   * Carried as its own fact rather than inferred from `plans.length > 1`,
   * which happens to select the same two roles today and would stop the moment
   * the consultation got a second rate. "How many people are on the shoot" is
   * a question about a SHOOT; asking it because a role has two price tiers
   * would put it on a channel audit.
   */
  isShootDay: boolean
  /**
   * Days each plan can still start on, `YYYY-MM-DD`, keyed by plan slug.
   *
   * This is what performers declare in Przepustowość, read through
   * `core.capacity.openDaysFor` — so a Saturday somebody put in their calendar
   * is bookable and a Tuesday nobody did is not. The page used to decide that
   * for itself with `weekday === 0 || weekday === 6`, which no amount of
   * declaring could override.
   *
   * THREE states, and collapsing any two of them is a bug:
   *
   * `null` — not asked, or the capacity read failed. The caller must fall back
   * to its old behaviour, because a timed-out aggregate must never render as
   * "the team is booked out".
   * an EMPTY list for a plan — asked, and nobody has room in the horizon. That
   * is a real answer and the calendar says so.
   * days — those days, weekends included where somebody declared one.
   */
  openDaysByPlan: Record<string, readonly string[]> | null

  /**
   * Which HOURS each plan may start at, per day — the same three-state rule as
   * `openDaysByPlan` above, one level deeper.
   *
   * The days say how much capacity is LEFT; these say when anybody is actually
   * there, read from `performer_availability` through
   * `core.availability.offerableStartsFor`. Neither implies the other: a
   * photographer with a free day declared as 09:00–12:00 has room for a half
   * plan and none at all for a full one.
   *
   * The owner's rule has two halves — *„przy proponowaniu LUB przydzielaniu"*
   * — and this is the proposing one. Without it the picker offers 11:00 off a
   * fixed list, the customer pays, and the claim refuses them at the till.
   *
   * `null` — not asked or the read failed, so the caller falls back to
   * `planStarts`. A day PRESENT with an empty list means nobody can start that
   * plan then, which is a real answer; a day absent means the same as above.
   */
  openStartsByPlan: Record<string, Record<string, readonly string[]>> | null
}

/**
 * The glyphs a specialist may wear.
 *
 * A closed set, edited by name: the card draws it at 17px inside a gradient
 * disc, where an uploaded photograph would read as a smudge, and a free-text
 * icon field would be a way to render nothing at all. Adding one is a line here
 * plus the option below — no migration, because the column stores the name.
 */
export const SPECIALIST_ICONS: Record<string, LucideIcon> = {
  youtube: YoutubeGlyph,
  camera: Camera,
  video: Video,
  clapperboard: Clapperboard,
  megaphone: Megaphone,
  mic: Mic,
  monitor: MonitorPlay,
  pen: PenTool,
  scissors: Scissors,
  sparkles: Sparkles,
  users: Users,
}

/** The card always gets an icon: an unknown or missing name falls back. */
export function specialistIcon(name: string | null): LucideIcon {
  return (name ? SPECIALIST_ICONS[name] : undefined) ?? Sparkles
}

/**
 * A hue per role, keyed by the SAME name the icon is.
 *
 * The three cards in "Kogo potrzebujesz?" were the same dark rectangle three
 * times, told apart only by reading them. A colour the eye catches before the
 * word does is what turns a row of boxes into three distinct choices — and
 * keying it to the icon name means a role added in the panel is themed by the
 * glyph somebody already picked for it, with no second field to fill in and no
 * migration.
 *
 * ON THE BRAND ARC, and only on it. This map used to spread the eleven glyphs
 * around the whole colour wheel — a red, an amber, a teal, a blue, two greens
 * — on the argument that neighbouring magentas would be no more
 * distinguishable than three greys. The owner ruled otherwise, for the whole
 * site: decorative colour is the FLOU gradient or it is nothing. The argument
 * was wrong anyway; what separates these is lightness and chroma, which
 * `lib/brand-tones.ts` varies far more than it varies hue.
 */
const SPECIALIST_STOPS: Record<string, number> = {
  youtube: 0,
  camera: 3,
  video: 6,
  clapperboard: 1,
  megaphone: 4,
  mic: 7,
  monitor: 2,
  pen: 5,
  scissors: 8,
  sparkles: 1,
  users: 4,
}

/** The first stop stands in for a glyph nobody has themed yet. */
export function specialistTone(name: string | null): string {
  return brandTone((name ? SPECIALIST_STOPS[name] : undefined) ?? 0)
}

/**
 * Cities to offer for a specialist.
 *
 * With nothing picked this used to return every row in `city`, which was only
 * ever right by accident: it assumed the city table and the cities somebody
 * actually travels to were the same list. They came apart the moment Poznań
 * came off the camera operator — it was the only role that listed it, so the
 * opening map went on drawing a pin for a city no specialist serves, under
 * copy promising "do pozostałych miast dojeżdżamy".
 *
 * So the default is DERIVED from who covers what. The city row stays in the
 * database either way — taking it out would mean the panel could not put
 * somebody back there — and the pin returns by itself the moment one does.
 *
 * `"online"` yields none, because the caller renders the whole-country state
 * instead of a pin list.
 */
export function citiesFor(
  specialist: SpecialistView | undefined,
  cities: CityView[],
  /** Everyone on offer. Read only when nothing is picked yet. */
  offered: SpecialistView[],
): CityView[] {
  if (!specialist) {
    const served = new Set(offered.flatMap((s) => (s.coverage === 'online' ? [] : s.coverage)))
    return cities.filter((c) => served.has(c.id))
  }
  if (specialist.coverage === 'online') return []
  const covered = new Set(specialist.coverage)
  return cities.filter((c) => covered.has(c.id))
}
